// utils/firestoreUtils.js
import { doc, collection, getDoc, updateDoc, runTransaction, Timestamp } from 'firebase/firestore';
import { auth, firestore } from '../firebaseConfig';
import axios from 'axios';
import { backendURL } from '../App';

/**
 * Update user settings in Firestore.
 * @param {string} userId - The ID of the user.
 * @param {object} settings - The settings data to update.
 * @returns {Promise<void>} - A promise that resolves when the update is complete.
 */
export const updateUserSettings = async (userId, settings) => {
  const userDocRef = doc(firestore, 'users', userId);

  // This object will hold the fields to update using dot notation.
  const dotNotationPayload = {};

  // A map to translate front-end state names to Firestore field names.
  const settingsMap = {
    sentencesPerDay: 'settings.sentences_per_day',
    sentenceReviewsPerDay: 'settings.sentence_reviews_per_day',
    wordsReviewPerDay: 'settings.words_reviews_per_day',
    sentenceUnlocking: 'settings.sentence_unlocking',
    wkLevelMin: 'settings.wk_level_min',
    wkLevelMax: 'settings.wk_level_max',
    reviewFrequency: 'settings.review_frequency',
    reviewBurnedItems: 'settings.review_burned_items',
    timezone: 'settings.timezone',
    getWeeklyReports: 'settings.get_weekly_reports',
    levelsToLearnFrom: 'settings.levels_to_learn_from',
  };

  // Iterate over the provided settings from the form
  for (const key in settings) {
    // Check if the key exists in our map (e.g., 'sentencesPerDay')
    if (settingsMap[key]) {
      const firestoreKey = settingsMap[key];
      // Add the update to our payload, e.g., { 'settings.sentences_per_day': 5 }
      dotNotationPayload[firestoreKey] = settings[key];
    }
  }

  // --- SPECIAL HANDLING FOR VACATION MODE ---
  // Only add the vacation_started_at field to the payload if it's a valid, truthy value.
  // If `settings.vacationStartedAt` is `null` (because the switch was turned off),
  // this condition will be false, and the field will be left untouched in Firestore.
  if (settings.vacationStartedAt) {
    dotNotationPayload['settings.vacation_started_at'] = settings.vacationStartedAt;
  }

  // Ensure there's something to update to avoid an unnecessary write.
  if (Object.keys(dotNotationPayload).length > 0) {
    await updateDoc(userDocRef, dotNotationPayload);
  }
};

export const cancelSubscription = async (userId) => {
  try {
    // Reference the user's subscription document in Firestore
    const userSubscriptionRef = doc(firestore, 'users_subscriptions', userId);
    const docSnapshot = await getDoc(userSubscriptionRef);

    if (docSnapshot.exists()) {
      const subscriptionData = docSnapshot.data();
      const subscriptionId = subscriptionData.subscription_id;

      // Make the request to your backend service to cancel the subscription
      const response = await axios.post(`${backendURL}/cancelUserSubscription`, { userId: userId, subscriptionId: subscriptionId });

      // Check the response from your backend to make sure the cancellation was successful
      if (response.status === 200) {
        return true; // Return true indicating that the cancellation was successful
      } else {
        console.error(`Failed to cancel subscription for user ${userId}. Response:`, response.data);
        return false; // Return false indicating that the cancellation was unsuccessful
      }
    } else {
      console.warn(`No subscription data found for user with ID: ${userId}`);
      return false; // Return false if the user has no subscription data
    }
  } catch (error) {
    console.error(`Error canceling subscription for user ${userId}:`, error);
    return false; // Return false in case of any error
  }
};

/**
 * Default contents of every users/{uid}/metadata doc, keyed by doc ID.
 * Used at registration and by the Danger Zone reset flows — keep in sync
 * with what MetadataProvider expects to find.
 */
export const buildDefaultMetadataDocs = () => {
  const emptyWkBuckets = {};
  for (let wk = 0; wk <= 61; wk += 1) emptyWkBuckets[wk] = 0;
  const emptySentencesByWk = {};
  for (const jlpt of [1, 2, 3, 4, 5]) emptySentencesByWk[jlpt] = { ...emptyWkBuckets };

  return {
    user_words_ids: { data: [] },
    user_sentences_ids: { data: [] },
    learned_today: { data: {} },
    reviewed_words_today: { data: {} },
    reviewed_sentences_today: { data: {} },
    counts_by_level: {
      sentences_by_wk: emptySentencesByWk,
      words: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
    },
    counts_by_srs: {
      sentences: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, skipped: 0 },
      words: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, skipped: 0 },
    },
    review_accuracy: {
      sentences: { correct: 0, wrong: 0 },
      words: { correct: 0, wrong: 0 },
    },
    // seconds → total listening time (review + learn).
    listening_stats: {
      sentences: { seconds: 0 },
      words: { seconds: 0 },
    },
    // Plays-per-day → numerator of the "avg listens before answering" ratio
    // (submissions = reviewed_*_today). Mirrors the reviewed_*_today daily docs.
    played_words_today: { data: {} },
    played_sentences_today: { data: {} },
  };
};

/**
 * Thrown when registerUser fails. Lets callers distinguish a profile
 * initialization failure (Auth account exists, Firestore doc doesn't)
 * from Firebase Auth errors, which carry a `.code` property.
 */
export class RegistrationError extends Error {
  constructor(uid, originalError) {
    super(`Registration failed for user ${uid}. Original error: ${originalError?.message || originalError}`);
    this.name = 'RegistrationError';
    this.originalError = originalError;
  }
}

export const registerUser = async (uid, email) => {
  if (!uid || !email) {
    // It's better practice to reject the promise than throw synchronously in an async function
    // unless it's a programmer error (like missing args here).
    return Promise.reject(new Error('Email and UID are required'));
  }

  const userDocRef = doc(firestore, 'users', uid);

  try {
    // --- Use a Transaction for atomic check-and-set ---
    await runTransaction(firestore, async (transaction) => {
      const userDocSnap = await transaction.get(userDocRef);

      if (userDocSnap.exists() && userDocSnap.data()?.email) {
        return; // Full doc already exists, exit transaction
      }

      // Get current auth user's email verification status
      const currentUser = auth.currentUser;
      const emailVerified = currentUser ? currentUser.emailVerified : false;

      if (userDocSnap.exists()) {
        // Partial doc (exists but has no email): repair only the identity
        // fields. The user may have real settings and metadata — writing
        // defaults or re-initializing metadata here would wipe their data.
        transaction.set(userDocRef, { email, email_verified: emailVerified }, { merge: true });
        return;
      }

      // Doc truly missing — create it with defaults
      const currTimestamp = Timestamp.now(); // Use server timestamp

      const newUserDefaults = {
        email,
        email_verified: emailVerified, // Set from Firebase Auth
        finished_tour: false,
        finished_session_tour: false,
        settings: {
          sentences_per_day: 5,
          sentence_reviews_per_day: -1,
          words_reviews_per_day: -1,
          sentence_unlocking: 'level',
          wk_level_min: null,
          wk_level_max: null,
          review_frequency: 'day',
          review_burned_items: false,
          timezone: 'UTC',
          get_weekly_reports: true,
          levels_to_learn_from: [5, 4, 3, 2, 1],
          // vacation_started_at: null, // Explicitly null or leave out
        },
        created_at: currTimestamp, // Timestamp from server
      };

      // Queue the set operation within the transaction
      transaction.set(userDocRef, newUserDefaults, { merge: true });

      // Initialize all metadata subcollection docs so MetadataProvider never waits on missing docs
      const metadataRef = collection(firestore, 'users', uid, 'metadata');
      for (const [docId, data] of Object.entries(buildDefaultMetadataDocs())) {
        transaction.set(doc(metadataRef, docId), data);
      }
    }); // --- End of Transaction ---

    return {
      message: 'User registration processed successfully (either created or already existed).',
      uid: uid,
    };
  } catch (error) {
    // This block executes ONLY if the runTransaction itself failed.
    // This could be due to network errors, permission issues, internal Firestore errors, etc.
    // It does NOT execute if the user simply already existed (that case exits the transaction callback cleanly).
    console.error(`[${uid}] Transaction FAILED during registration attempt:`, error);

    // Never delete the Auth user here: this runs in recently-authenticated
    // sessions where the delete would succeed and silently destroy a live
    // account. Recovery on the next sign-in (useAuth) re-runs registerUser.
    throw new RegistrationError(uid, error);
  }
};

/**
 * Mark the user as having completed the tour in Firestore.
 * @param {string} userId - The ID of the user.
 * @returns {Promise<void>} - A promise that resolves when the update is complete.
 */
export const markTourAsFinished = async (userId) => {
  try {
    const userDocRef = doc(firestore, 'users', userId);

    await updateDoc(userDocRef, {
      finished_tour: true, // Add or update the field
    });
  } catch (error) {
    console.error(`Error updating tour completion status for user ${userId}:`, error);
    throw error;
  }
};

/**
 * Mark the user as having completed the session tour in Firestore.
 * @param {string} userId - The ID of the user.
 * @returns {Promise<void>} - A promise that resolves when the update is complete.
 */
export const markSessionTourAsFinished = async (userId) => {
  try {
    const userDocRef = doc(firestore, 'users', userId);

    await updateDoc(userDocRef, {
      finished_session_tour: true,
    });
  } catch (error) {
    console.error(`Error updating session tour completion status for user ${userId}:`, error);
    throw error;
  }
};
